import { Button, Row, Col, Form, Input, Tag } from "antd";
import useModal from "../../hooks/useModal";
import { MdEdit, MdLink } from "react-icons/md";
import { FaInfoCircle } from "react-icons/fa";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useGetJobsByGroup, useGetalAllJobs } from "../../hooks/useGetJobs";
import { Loading } from "ui/pages/app/planejamentoOperacional/components/AgendaCampo/Loading";
import { AllJobsResponse, JobResponse } from "../../types";
import { CustomModal, Header, StyledSelect, Tags, Title } from "./style";
import useUpdateJobGroup, {
  UpdateJobGroupProps,
} from "../../hooks/useUpdateJobGroup";
import { showError, showSuccess } from "infra/helpers/alerta";
import useLinkJobOnGroup from "../../hooks/useLinkJobOnGroup";
import useUnlinkJobsOnGroups from "../../hooks/useUnlinkJobsOnGroups";
import useCreateGroupsJobs, {
  CreateGroupsJobsRequest,
} from "../../hooks/useCreateGroupsJobs";

interface CreateEditModalProps {
  refetch: () => void;
}

const { Item, useForm } = Form;

function CreateEditModal({ refetch }: CreateEditModalProps) {
  const { isOpen, setIsOpen, isEditing, id, setIsEditing, setId } = useModal();
  const { mutateAsync: updateJobGroupAsync } = useUpdateJobGroup();
  const { mutateAsync: linkJobOnGroupAsync } = useLinkJobOnGroup();
  const { mutateAsync: unlinkJobsOnGroupsAsync } = useUnlinkJobsOnGroups();
  const { mutateAsync: createGroupsJobsAsync } = useCreateGroupsJobs();
  const { data, isLoading } = useGetJobsByGroup({
    groupId: id ?? "",
  });
  const { data: allJobs, refetch: refetchAllJobs } = useGetalAllJobs();

  const [jobs, setJobs] = useState<JobResponse[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [form] = useForm();

  const defineTitleModal = useMemo((): string => {
    if (isEditing) {
      return "Editar";
    }

    return "Novo";
  }, [isEditing]);

  const prefillForm = useCallback(() => {
    if (isEditing) {
      form.setFieldsValue({
        nome: data?.nome,
        ativo: data?.ativo ? "ativo" : "inativo",
      });

      setJobs(data?.jobs ?? []);
      return;
    }

    form.setFieldsValue({
      nome: "",
      ativo: "ativo",
    });

    setJobs([]);
  }, [data, isEditing, form]);

  useEffect(() => {
    if (!isOpen) return;

    prefillForm();
  }, [isOpen, prefillForm]);

  const handleCancel = () => {
    setIsOpen(false);
    setIsEditing(false);
    setId(null);

    form.resetFields();
    setJobs([]);
  };

  const jobsOptions = useMemo(() => {
    return (
      allJobs?.data
        ?.sort((a: AllJobsResponse, b: AllJobsResponse) =>
          a.jobId.localeCompare(b.jobId),
        )
        .map((job: AllJobsResponse) => ({
          label: `${job.jobId}`,
          value: job.jobId,
          disabled: job.vinculado && job.recurringJobGroupId !== id,
        })) ?? []
    );
  }, [allJobs, id]);

  const handleSubmit = async (values: any) => {
    if (isEditing) {
      return updateJobGroup(values);
    }

    return createGroupsJobs(values);
  };

  const updateJobGroup = async (values: any) => {
    setIsSubmitting(true);

    try {
      const body: UpdateJobGroupProps = {
        recurringJobGroupId: id ?? "",
        nome: values.nome,
        ativo: values.ativo === "ativo",
      };

      const originalJobIds =
        data?.jobs?.map((job: JobResponse) => job.jobId) ?? [];

      const currentJobIds = jobs.map((job: JobResponse) => job.jobId);

      const addedJobIds = currentJobIds.filter(
        (jobId) => !originalJobIds.includes(jobId),
      );

      const removedJobIds = originalJobIds.filter(
        (jobId: string) => !currentJobIds.includes(jobId),
      );

      var requests = [];

      if (removedJobIds.length > 0) {
        requests.push(
          unlinkJobsOnGroupsAsync({
            recurringJobGroupId: id ?? "",
            jobIds: removedJobIds,
          }),
        );
      }

      if (addedJobIds.length > 0) {
        requests.push(
          linkJobOnGroupAsync({
            recurringJobGroupId: id ?? "",
            jobIds: addedJobIds,
          }),
        );
      }

      requests.push(updateJobGroupAsync(body));

      await Promise.all(requests);

      showSuccess("Grupo de Jobs atualizado com sucesso");
      refetchAllJobs();
      refetch();
      handleCancel();
    } catch (error) {
      showError("Erro ao atualizar grupo de jobs");
    } finally {
      setIsSubmitting(false);
    }
  };

  const createGroupsJobs = async (values: any) => {
    setIsSubmitting(true);

    try {
      const body: CreateGroupsJobsRequest = {
        nome: values.nome,
        ativo: values.ativo === "ativo" ? true : false,
        jobIds: jobs.map((job: JobResponse) => job.jobId),
      };

      await createGroupsJobsAsync(body);

      showSuccess("Grupo de Jobs criado com sucesso");

      refetchAllJobs();
      refetch();
      handleCancel();
    } catch (error) {
      showError("Erro ao criar grupo de jobs");
    } finally {
      setIsSubmitting(false);
    }
  };

  const addJobs = (value: string[]) => {
    const selectedJobs =
      allJobs?.data?.filter((job: AllJobsResponse) =>
        value.includes(job.jobId),
      ) ?? [];

    setJobs(selectedJobs);
  };

  const handleRemoveJob = (job: JobResponse) => {
    setJobs((prev) => prev.filter((j) => j.jobId !== job.jobId));
  };

  return (
    <CustomModal
      visible={isOpen}
      onCancel={handleCancel}
      maskClosable={false}
      width={700}
      title={
        <Header>
          <MdEdit size={18} />
          {defineTitleModal} Grupo de Jobs
        </Header>
      }
      footer={
        <>
          <Button
            disabled={isSubmitting}
            loading={isSubmitting}
            size="large"
            onClick={handleCancel}
            style={{ borderRadius: 8 }}
          >
            Cancelar
          </Button>
          <Button
            disabled={isSubmitting}
            loading={isSubmitting}
            size="large"
            type="primary"
            htmlType="submit"
            form="job-group-form"
            style={{ borderRadius: 8 }}
          >
            Salvar
          </Button>
        </>
      }
    >
      <Title>
        <FaInfoCircle size={18} /> Informações Básicas
      </Title>

      {isLoading ? (
        <Loading />
      ) : (
        <>
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            id="job-group-form"
          >
            <Row gutter={[16, 16]}>
              <Col span={8}>
                <Item
                  label="Nome"
                  name="nome"
                  rules={[{ required: true, message: "Nome é obrigatório" }]}
                >
                  <Input
                    disabled={isSubmitting}
                    size="large"
                    placeholder="Descrição"
                    style={{ borderRadius: 8 }}
                  />
                </Item>
              </Col>
              <Col span={8}>
                <Item
                  label="Status"
                  name="ativo"
                  rules={[{ required: true, message: "Status é obrigatório" }]}
                >
                  <StyledSelect
                    disabled={isSubmitting}
                    size="large"
                    placeholder="Status"
                    options={[
                      { label: "Ativo", value: "ativo" },
                      { label: "Inativo", value: "inativo" },
                    ]}
                    style={{ borderRadius: 8 }}
                  />
                </Item>
              </Col>
            </Row>
          </Form>
        </>
      )}

      <Title>
        <MdLink size={22} /> Vincular Jobs
      </Title>
      <StyledSelect
        showSearch
        size="large"
        mode="multiple"
        disabled={isSubmitting}
        loading={isSubmitting}
        optionFilterProp="label"
        autoClearSearchValue={false}
        maxTagCount={0}
        maxTagPlaceholder={(values) => `${values.length} jobs`}
        options={jobsOptions}
        placeholder="Jobs"
        value={jobs.map((job: JobResponse) => job.jobId)}
        onChange={(value) => {
          addJobs(value as string[]);
        }}
        style={{ width: "100%" }}
      />

      <Tags>
        {jobs.map((job: JobResponse) => (
          <Tag
            key={job.jobId}
            color="processing"
            style={{ cursor: "pointer", margin: 0 }}
            onClick={() => {
              if (isSubmitting) {
                return;
              }

              handleRemoveJob(job);
            }}
          >
            {job.jobId} <b>X</b>
          </Tag>
        ))}
      </Tags>
    </CustomModal>
  );
}

export default CreateEditModal;
